import { Box, Typography, Grid, CircularProgress, Divider, } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import axios from 'axios';
import React, { useState, useEffect } from "react";
import { GET_INVOICE_EXPORT_PROFORMA_INVOICE, READ_COA_EXPORT } from '../../../services/apiRoutes';
import CustomCardView from '../../common/CustomCardView';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LocalPrintshopOutlinedIcon from '@mui/icons-material/LocalPrintshopOutlined';
import DetailsParticulars from '../../common/DetailsPage/DetailsParticulars';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import CustomPrintModel from '../../common/CustomPrint';
import { printFunction } from '../../../services/apiServices';
import { genericPost } from '../../../services/authServices';
import { CoaPrintFirst, CoaPrintFour, CoaPrintSecond, CoaPrintThird } from '../PrintPdf/CoaPrint';
import { useCompanyDateFormat } from "../../../constants/useCompanyDateFormat";
import { t } from '../../../constants/translationHelper';

const COAView = ({ handleClose, setMessage, fetchData, coaId, companyName, phoneNumber, shipmentOrgId, templateId }) => {
    const companyDateFormat = useCompanyDateFormat();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [termConditions, setTermConditions] = useState([]);
    const [companyBanks, setCompanyBanks] = useState([]);
    const [shipments, setShipments] = useState([]);
    const [shippingBooking, setShippingBooking] = useState([]);
    const [paymentVoucher, setPaymentVoucher] = useState([]);
    const [companyTaxes, setCompanyTaxes] = useState([]);
    const [shippingmark, setShippingmark] = useState([]);
    const [packingList, setPackingList] = useState([]);
    const [salesContracts, setSalesContracts] = useState([]);

    useEffect(() => {
        fetchDataInvoice();
    }, []);

    const fetchDataInvoice = async () => {
        setLoading(true);
        try {
            const payload = { shipmentOrgId: shipmentOrgId };
            const response = await genericPost(GET_INVOICE_EXPORT_PROFORMA_INVOICE, payload);
            if (response?.data?.success) {
                const resData = response.data.data;
                setTermConditions(resData.termConditions || []);
                setCompanyBanks(resData.companyBanks || []);
                setShipments(resData.shipments || []);
                setShippingBooking(resData.shippingBooking || []);
                setPaymentVoucher(resData.paymentVoucher || []);
                setCompanyTaxes(resData.companyTaxes || []);
                setShippingmark(resData.shippingmark || []);
                setPackingList(resData.packingList || []);
                setSalesContracts(resData.salesContracts || []);
            } else {
                setError('No data found');
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error fetching data');
        } finally {
            setLoading(false);
        }
    };


    // Fetch data for the commercial invoice
    useEffect(() => {
        if (!coaId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_COA_EXPORT(coaId), {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                setData(response.data.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [coaId]);

    if (loading) return <CircularProgress />;
    if (error) return <p>Error: {error}</p>;

    const formatPort = (port, country) => [port, country].filter(Boolean).join(", ");

    // Initialize form values
    const values = {
        coaId: data?.coaId || '',
        userId: data?.userId || '',
        companyId: data?.companyId || '',
        clientCompanyId: data?.clientCompanyId || '',
        toAddress: data?.toAddress || '',
        invoiceNo: data?.invoiceNo || '',
        date: companyDateFormat(data?.date) || '',
        loadingPort: formatPort(
            data?.loading?.portName,
            data?.loading?.country?.name
        ),
        destinationPort: formatPort(
            data?.destination?.portName,
            data?.destination?.country?.name
        ),

        productName: data?.product?.productName || '',
        conculation: data?.conculation || '',
        analyst: data?.analyst || '',
        examiner: data?.examiner || '',
        approved: data?.approved || '',
        casNo: data?.casNo || '',
        iupac: data?.iupac || '',
        particulars: data?.particulars || [],
    };

    const labels = [
        { key: 'toAddress', label: 'To', sm: 12, md: 6, },
        { key: 'invoiceNo', label: 'Invoice No', sm: 12, md: 3, },
        { key: 'date', label: 'Date', sm: 12, md: 3, },
        { key: 'loadingPort', label: 'Loading Port', sm: 12, md: 6, sx: { pt: 2 } },
        { key: 'destinationPort', label: 'Destination Port', sm: 12, md: 6, sx: { pt: 2 } },
        { key: 'productName', label: 'Product Name', sm: 12, md: 6, sx: { pt: 2 } },
    ];
    const labelsFooter = [
        { key: 'analyst', label: 'Analyst', sm: 12, md: 6, },
        { key: 'examiner', label: 'Examiner', sm: 12, md: 6, },
        { key: 'casNo', label: 'CAS No', sm: 12, md: 6, sx: { pt: 2 } },
        { key: 'iupac', label: 'IUPAC', sm: 12, md: 6, sx: { pt: 2 } },
        { key: 'approved', label: 'Approved', sm: 12, md: 6, sx: { pt: 2 } },
        { key: 'conculation', label: 'Conclusion', sm: 12, md: 7, sx: { pt: 2 } },
    ];

    const particularsColumns = [
        { id: 'items', label: 'Items', accessor: 'items' },
        { id: 'standard', label: 'Standard', accessor: 'standard' },
        { id: 'result', label: 'Result', accessor: 'result' },
        // { id: 'levelId', label: 'Level', accessor: 'levelId' },
    ];
    const clientCompanyName = data?.customer?.clientCompanyName || '';
    const encodedCompanyName = clientCompanyName.replace(/ /g, '').toLowerCase();
    const fileName = `commercial_invoice_${encodedCompanyName}.pdf`;

    const printContent = () => {
        const elementId = 'contentId';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId, fileName);
    };
    const pdfContent = () => {
        const elementId = 'contentpdf';
        const iframeId = 'print-iframe';
        printFunction(elementId, iframeId, fileName);
    };

    const invoiceTermConditions = termConditions?.find(
        (item) => item.voucherName === "7"
    )?.termConditions || "";
    // const check = parseInt(templateId);
    const footerFalse = false;
    const footerTrue = true;

    const check = parseInt(templateId);

    const getPrintContent = () => {
        if (check === 1) {
            return (<CoaPrintFirst
                invoiceNo={values.invoiceNo}
                invoiceData={data}
                termConditions={invoiceTermConditions}
                companyBanks={companyBanks}
                shippingBooking={shippingBooking}
                shipments={shipments}
                paymentVoucher={paymentVoucher}
                companyTaxes={companyTaxes}
                footer={footerFalse} />);
        }
        if (check === 2) {
            return (
                <CoaPrintSecond
                    invoiceNo={values.invoiceNo}

                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    packingList={packingList}
                    salesContracts={salesContracts}
                    footer={footerFalse} />);
        }
        if (check === 3) {
            return (
                <CoaPrintFour
                    invoiceNo={values.invoiceNo}

                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerFalse} />);
        }
        if (check === 4) {
            return (
                <CoaPrintFour
                    invoiceNo={values.invoiceNo}

                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerFalse} />);
        }

        return <CoaPrintFirst
            invoiceNo={values.invoiceNo}

            invoiceData={data}
            termConditions={invoiceTermConditions}
            companyBanks={companyBanks}
            shippingBooking={shippingBooking}
            shipments={shipments}
            paymentVoucher={paymentVoucher}
            companyTaxes={companyTaxes}
            footer={footerFalse} />;
    };

    const getPdfContent = () => {
        if (check === 1) {
            return (<CoaPrintFirst
                invoiceNo={values.invoiceNo}

                invoiceData={data}
                termConditions={invoiceTermConditions}
                companyBanks={companyBanks}
                shippingBooking={shippingBooking}
                shipments={shipments}
                paymentVoucher={paymentVoucher}
                companyTaxes={companyTaxes}
                footer={footerTrue} />);
        }
        if (check === 2) {
            return (
                <CoaPrintSecond
                    invoiceNo={values.invoiceNo}

                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    packingList={packingList}
                    salesContracts={salesContracts}
                    footer={footerTrue} />);
        }
        if (check === 3) {
            return (
                <CoaPrintFour
                    invoiceNo={values.invoiceNo}

                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerTrue} />);
        }
        if (check === 4) {
            return (
                <CoaPrintFour
                    invoiceNo={values.invoiceNo}

                    invoiceData={data}
                    termConditions={invoiceTermConditions}
                    companyBanks={companyBanks}
                    shippingBooking={shippingBooking}
                    shipments={shipments}
                    paymentVoucher={paymentVoucher}
                    companyTaxes={companyTaxes}
                    shippingmark={shippingmark}
                    footer={footerTrue} />);
        }

        return <CoaPrintFirst
            invoiceNo={values.invoiceNo}

            invoiceData={data}
            termConditions={invoiceTermConditions}
            companyBanks={companyBanks}
            shippingBooking={shippingBooking}
            shipments={shipments}
            paymentVoucher={paymentVoucher}
            companyTaxes={companyTaxes}
            footer={footerTrue} />;
    };

    return (
        <>

            <CustomPrintModel contentId="contentId">
                {getPrintContent()}
            </CustomPrintModel >

            <CustomPrintModel contentId="contentpdf">
                {getPdfContent()}
            </CustomPrintModel >

            <Box sx={{ mx: 'auto', mt: 0 }}>
                <Formik initialValues={values} enableReinitialize>
                    {() => (
                        <Form>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography sx={{ fontWeight: 600, fontSize: "18px", color: "#212529" }}>
                                        {t("COA")}
                                    </Typography>
                                </Grid>
                                <Grid item xs={8} style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Box>
                                        <IconButton aria-label="delete">
                                            <DeleteOutlineOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={printContent}>
                                            <LocalPrintshopOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="print" onClick={pdfContent}>
                                            <PictureAsPdfOutlinedIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                        <IconButton aria-label="close" onClick={handleClose} >
                                            <CloseIcon sx={{ fontSize: '20px', color: '#007bff' }} />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Divider />
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <Box style={{ margin: "auto", padding: "auto", textAlign: "center" }}>
                                        <Typography style={{ color: "#212529", fontWeight: 500, fontSize: "15px", textTransform: "uppercase" }}>
                                            {t("Certification Of Analysis")}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <CustomCardView label={labels} data={values} />
                                </Grid>

                                <Grid item xs={12}>
                                    <Box display="flex" flexDirection="row" alignItems="center">
                                        <Typography style={{ fontWeight: 700, color: "#212529", fontSize: "16px", marginLeft: "4px" }}>
                                            {t("Particulars")}
                                        </Typography>
                                    </Box>
                                    <DetailsParticulars
                                        title={'Particulars'}
                                        columns={particularsColumns}
                                        data={data?.particulars}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                    <CustomCardView label={labelsFooter} data={values} />
                                </Grid>

                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Box>
        </>
    );
};

export default COAView;